import { useLocale, useLocaleString } from '../../../hooks/useLocale';
import { getUseStateSelector } from '../../../store';
import {
  PropertyServiceTypeModel,
  PropertyStatusModel,
  PropertyTypeModel,
} from '../../Property/ManageCreateProperty/models/property_type.model';
import { PropertyUnitModel } from '../../Property/ManageCreateProperty/models/property_unit.model';
import { PropertyImageFile, PropertyOwnershipType } from './property.model';
import { ShortUserModel } from './short_user.model';

interface IPropertyFluctuation {
  id: number;
  created_at: Date;
  modified_at: Date;
  upper_limit: number;
  lower_limit: number;
  property: number;
}

export interface IPropertyFile {
  id: number;
  property_file_type: number;
  created_at: Date;
  modified_at: Date;
  file: string;
  property: number;
}

interface IPropertyDetailsModel {
  ownership_type: PropertyOwnershipType;
  is_listing_enabled: boolean;
  property_long_name: string;
  property_trading_name: string;
  property_city: string;
  property_locality: string;
  google_maps_address: string;
  property_type: PropertyTypeModel;
  property_description: string;
  initial_unit_price: number;
  total_units: number;
  created_by: ShortUserModel;
  property_manager: ShortUserModel;
  property_status: PropertyStatusModel;
  property_images: PropertyImageFile[];
  property_fluctuation: IPropertyFluctuation[];
  property_files: IPropertyFile[];
  property_units: PropertyUnitModel[];
  is_crowd_funding: boolean;
  is_trade_blocked: boolean;
  local_trade_only: boolean;
  approved_by: ShortUserModel;
  service_type: PropertyServiceTypeModel;
  property_long_name_en: string;
  property_trading_name_en: string;
  property_city_ar: string;
  property_locality_ar: string;
  pincode: string;
  country_ar: string;
  property_description_en: string;
  min_buy_order_value: number;

  //
  id: number;
}

export class PropertyDetailsModel implements IPropertyDetailsModel {
  ownership_type: PropertyOwnershipType;
  is_listing_enabled: boolean;
  property_long_name: string;
  property_trading_name: string;
  property_city: string;
  property_locality: string;
  google_maps_address: string;
  property_type: PropertyTypeModel;
  property_description: string;
  initial_unit_price: number;
  total_units: number;
  created_by: ShortUserModel;
  property_manager: ShortUserModel;
  property_status: PropertyStatusModel;
  property_images: PropertyImageFile[];
  property_fluctuation: IPropertyFluctuation[];
  property_files: IPropertyFile[];
  property_units: PropertyUnitModel[];
  is_crowd_funding: boolean;
  is_trade_blocked: boolean;
  local_trade_only: boolean;
  approved_by: ShortUserModel;
  service_type: PropertyServiceTypeModel;
  property_long_name_en: string;
  property_trading_name_en: string;
  property_city_ar: string;
  property_locality_ar: string;
  pincode: string;
  country_ar: string;
  property_description_en: string;
  min_buy_order_value: number;

  //
  id: number;

  constructor(data: Partial<IPropertyDetailsModel>) {
    this.ownership_type =
      data.ownership_type ?? PropertyOwnershipType.fractional_ownership;
    this.is_listing_enabled = data.is_listing_enabled ?? false;
    this.property_long_name = data.property_long_name ?? '';
    this.property_trading_name = data.property_trading_name ?? '';
    this.property_city = data.property_city ?? '';
    this.property_locality = data.property_locality ?? '';
    this.google_maps_address = data.google_maps_address ?? '';
    this.property_type =
      data.property_type ?? new PropertyTypeModel({ id: 0, name: '', ar_name: '' });
    this.property_description = data.property_description ?? '';
    this.initial_unit_price = data.initial_unit_price ?? 0;
    this.total_units = data.total_units ?? 0;
    this.created_by =
      data.created_by ??
      new ShortUserModel({
        id: 0,
        english_name: '',
        arabic_name: '',
      });
    this.property_manager =
      data.property_manager ??
      new ShortUserModel({
        id: 0,
        english_name: '',
        arabic_name: '',
      });
    this.property_status =
      data.property_status ??
      new PropertyStatusModel({ id: 0, name: '', ar_name: '' });
    this.property_images = data.property_images ?? [];
    this.property_fluctuation = data.property_fluctuation ?? [];
    this.property_files = data.property_files ?? [];
    this.property_units = data.property_units ?? [];
    this.is_crowd_funding = data.is_crowd_funding ?? false;
    this.is_trade_blocked = data.is_trade_blocked ?? false;
    this.local_trade_only = data.local_trade_only ?? false;
    this.approved_by =
      data.approved_by ??
      new ShortUserModel({
        id: 0,
        english_name: '',
        arabic_name: '',
      });
    this.service_type =
      data.service_type ??
      new PropertyServiceTypeModel({ id: 0, name: '', ar_name: '' });
    this.property_long_name_en = data.property_long_name_en ?? '';
    this.property_trading_name_en = data.property_trading_name_en ?? '';
    this.property_city_ar = data.property_city_ar ?? '';
    this.property_locality_ar = data.property_locality_ar ?? '';
    this.pincode = data.pincode ?? '';
    this.country_ar = data.country_ar ?? '';
    this.property_description_en = data.property_description_en ?? '';
    this.min_buy_order_value = data.min_buy_order_value ?? 0;

    //
    this.id = data.id ?? 0;
  }

  /** from json to class instance */
  static fromJson(json: any, id: number): PropertyDetailsModel {
    return new PropertyDetailsModel({
      ...json,
      id,
      created_by: ShortUserModel.fromJson(json.created_by),
      property_manager: ShortUserModel.fromJson(json.property_manager),
      approved_by: ShortUserModel.fromJson(json.approved_by),
      property_type: PropertyTypeModel.fromJson(json.property_type),
      property_status: PropertyStatusModel.fromJson(json.property_status),
      service_type: PropertyServiceTypeModel.fromJson(json.service_type),
      property_units: json.property_units
        ? json.property_units.map((unit: any) => PropertyUnitModel.fromJson(unit))
        : [],

      property_fluctuation: (json.property_fluctuation ?? []).map((f: any) => ({
        ...f,
        created_at: new Date(f.created_at),
        modified_at: new Date(f.modified_at),
      })),
    });
  }

  /** from class instance to json */
  toJson() {
    return {
      ownership_type: this.ownership_type,
      is_listing_enabled: this.is_listing_enabled,
      property_long_name: this.property_long_name,
      property_trading_name: this.property_trading_name,
      property_city: this.property_city,
      property_locality: this.property_locality,
      google_maps_address: this.google_maps_address,
      property_type: this.property_type.toJson(),
      property_description: this.property_description,
      initial_unit_price: this.initial_unit_price,
      total_units: this.total_units,
      created_by: this.created_by.toJson(),
      property_manager: this.property_manager.toJson(),
      property_status: this.property_status.toJson(),
      property_images: this.property_images,
      property_fluctuation: this.property_fluctuation,
      property_files: this.property_files,
      property_units: this.property_units.map((unit) => unit.toJson()),
      is_crowd_funding: this.is_crowd_funding,
      is_trade_blocked: this.is_trade_blocked,
      local_trade_only: this.local_trade_only,
      approved_by: this.approved_by.toJson(),
      service_type: this.service_type.toJson(),
      property_long_name_en: this.property_long_name_en,
      property_trading_name_en: this.property_trading_name_en,
      property_city_ar: this.property_city_ar,
      property_locality_ar: this.property_locality_ar,
      pincode: this.pincode,
      country_ar: this.country_ar,
      property_description_en: this.property_description_en,
      min_buy_order_value: this.min_buy_order_value,
    };
  }

  get propertyName(): string {
    return useLocaleString(this.property_long_name, this.property_long_name_en);
  }

  get propertyTradingName(): string {
    return useLocaleString(
      this.property_trading_name,
      this.property_trading_name_en
    );
  }

  get propertyCity(): string {
    return useLocaleString(this.property_city_ar, this.property_city);
  }

  get propertyLocality(): string {
    return useLocaleString(this.property_locality_ar, this.property_locality);
  }

  get description(): string {
    return useLocaleString(this.property_description, this.property_description_en);
  }

  get isFractionalOwnership() {
    return this.ownership_type === PropertyOwnershipType.fractional_ownership;
  }

  get isSeperateOwnership() {
    return this.ownership_type === PropertyOwnershipType.separate_ownership;
  }

  get unitPrice() {
    return this.initial_unit_price === 0 ? undefined : this.initial_unit_price;
  }

  get isValidDto(): boolean {
    const isFractionalOwnership =
      this.ownership_type === PropertyOwnershipType.fractional_ownership;
    const isSeparateOwnership =
      this.ownership_type === PropertyOwnershipType.separate_ownership;
    const {
      separateOwnersUnits,
      fractionalOwnersUnits,
      imagesFiles,
      legalDocsFiles,
    } = getUseStateSelector((s) => s.propertyUnits);
    return (
      // Basic details
      this.property_long_name.trim() !== '' &&
        this.property_long_name_en.trim() !== '' &&
        this.property_trading_name.trim() !== '' &&
        this.property_trading_name_en.trim() !== '' &&
        this.property_city_ar.trim() !== '' &&
        this.property_city.trim() !== '' &&
        this.property_locality_ar.trim() !== '' &&
        this.property_locality.trim() !== '' &&
        this.pincode.trim() !== '' &&
        this.property_description.trim() !== '' &&
        this.property_description_en.trim() !== '' &&
        !!this.service_type &&
        !!this.property_type &&
        this.google_maps_address.trim() !== '' &&
        // Units details
        isFractionalOwnership
        ? fractionalOwnersUnits.length > 0 &&
            fractionalOwnersUnits.every((unit) => unit.isValidFODto)
        : separateOwnersUnits.length > 0 &&
            separateOwnersUnits.every((unit) => unit.isValidSODto) &&
            // Documents
            imagesFiles.length > 0 &&
            legalDocsFiles.length > 0
    );
  }

  getValidationErrors(): string[] {
    const errors: string[] = [];
    if (this.property_long_name.trim() === '') {
      errors.push('Property name in Arabic is required.');
    }
    if (this.property_long_name_en.trim() === '') {
      errors.push('Property name in English is required.');
    }
    if (this.property_trading_name.trim() === '') {
      errors.push('Trading name in Arabic is required.');
    }
    if (this.property_trading_name_en.trim() === '') {
      errors.push('Trading name in English is required.');
    }
    if (this.property_city_ar.trim() === '') {
      errors.push('City in Arabic is required.');
    }
    if (this.property_city.trim() === '') {
      errors.push('City in English is required.');
    }
    if (this.property_locality_ar.trim() === '') {
      errors.push('Locality in Arabic is required.');
    }
    if (this.property_locality.trim() === '') {
      errors.push('Locality in English is required.');
    }
    if (this.pincode.trim() === '') {
      errors.push('Postal code is required.');
    }
    if (this.property_description.trim() === '') {
      errors.push('Description in Arabic is required.');
    }
    if (this.property_description_en.trim() === '') {
      errors.push('Description in English is required.');
    }
    if (!this.service_type) {
      errors.push('Service type is required.');
    }
    if (!this.property_type) {
      errors.push('Property type is required.');
    }
    if (this.google_maps_address.trim() === '') {
      errors.push('Google Maps address is required.');
    }

    if (this.ownership_type === PropertyOwnershipType.fractional_ownership) {
      const { fractionalOwnersUnits } = getUseStateSelector((s) => s.propertyUnits);
      if (fractionalOwnersUnits.length === 0) {
        errors.push('At least one fractional ownership unit is required.');
      } else {
        fractionalOwnersUnits.forEach((unit, index) => {
          if (!unit.isValidFODto) {
            errors.push(`Fractional unit at index ${index + 1} is invalid.`);
          }
        });
      }
    } else if (this.ownership_type === PropertyOwnershipType.separate_ownership) {
      const { separateOwnersUnits } = getUseStateSelector((s) => s.propertyUnits);
      if (separateOwnersUnits.length === 0) {
        errors.push('At least one separate ownership unit is required.');
      } else {
        separateOwnersUnits.forEach((unit, index) => {
          if (!unit.isValidSODto) {
            errors.push(`Separate unit at index ${index + 1} is invalid.`);
          }
        });
      }
    }

    const { imagesFiles, legalDocsFiles } = getUseStateSelector(
      (s) => s.propertyUnits
    );
    if (imagesFiles.length === 0) {
      errors.push('At least one property image is required.');
    }
    if (legalDocsFiles.length === 0) {
      errors.push('At least one legal document is required.');
    }
    return errors;
  }

  /** 'copyWith' is the function to copy the instance of the class */
  copyWith(data: Partial<IPropertyDetailsModel>): PropertyDetailsModel {
    return new PropertyDetailsModel({
      ...this,
      ...data,
    });
  }

  get latLng(): { lat: number; lng: number } | undefined {
    if (!this.google_maps_address) return undefined;
    const [lat, lng] = this.google_maps_address.split(',').map(Number);
    if (isNaN(lat) || isNaN(lng)) return undefined;
    return { lat, lng };
  }
}
export default PropertyDetailsModel;
